/**
 * Canonical eight-tag list for the `/discover` vibe filter (WEB-62, PIP-80).
 *
 * Originally defined in `apps/web/config/vibes.ts`. Lifted here (PIP-80) so
 * the data-pipeline ingestors and the web app share a single source of truth
 * — adding or removing a vibe is a schema-level change and must not require
 * two places to stay in sync.
 *
 * The list is fixed for launch; the discover filter UI and `deriveVibes()`
 * heuristic both depend on this tuple's order and membership.
 */
export const VIBE_ORDER = [
  'quiet',
  'loud',
  'date-night',
  'solo-ok',
  'family',
  '21-plus',
  'outdoor',
  'free',
] as const;

export type VibeSlug = (typeof VIBE_ORDER)[number];

/**
 * Stable lookup set for valid vibe slugs (matches the `vibe=` URL multi-select).
 */
export const VALID_VIBES: ReadonlySet<string> = new Set<VibeSlug>(VIBE_ORDER);

/**
 * Shared URL query param name for the active vibe filter.
 */
export const VIBE_QUERY_PARAM = 'vibe';

/**
 * Display label for each vibe slug. Kept alongside the slug constant so every
 * surface (chips on event cards, debug menus, analytics dashboards) agrees on
 * the user-facing copy.
 */
export const VIBE_LABELS: Record<VibeSlug, string> = {
  quiet: 'Quiet',
  loud: 'Loud',
  'date-night': 'Date night',
  'solo-ok': 'Solo OK',
  family: 'Family',
  '21-plus': '21+',
  outdoor: 'Outdoor',
  free: 'Free',
};

/**
 * The "Free" vibe is a UX duplicate of the Discover price filter's `free`
 * preset (WEB-62 implementation notes; value updated by WEB-656 / DPL-06 when
 * the continuous price slider was replaced by three presets — Free / $0–25 /
 * $25+). Toggling Free cross-writes `price=free` — kept here so both the
 * picker and any future vibe-driven surface stay aligned.
 *
 * Prior to WEB-656 this was the literal `'0-0'` (the retired slider's
 * "Free only" shortcut range). `apps/web/lib/discover/pricePreset.ts`'s
 * `normalizePricePresetParam` still migrates a stray `price=0-0` from an old
 * bookmarked URL to the canonical `'free'` value, so this rename is
 * backward-compatible.
 *
 * WEB-661 owns properly describing/consuming the Free preset this cross-write
 * mirrors; this constant rename is the minimal change needed to keep the
 * existing coupling functionally correct now that `DiscoverParams.price` is a
 * canonical preset id rather than a free-form range string.
 */
export const FREE_VIBE: VibeSlug = 'free';
export const FREE_PRICE_RANGE = 'free';

/**
 * Narrow an arbitrary string to a `VibeSlug` when it matches one of the
 * canonical slugs. Returns `null` for anything else so callers can branch
 * cleanly (vs. casting + crossing fingers).
 */
export function asVibeSlug(value: string): VibeSlug | null {
  return VALID_VIBES.has(value) ? (value as VibeSlug) : null;
}
